interface Props {
  coins: number
  plan: "free" | "pro"
  compact?: boolean
}

/**
 * Static bank-card tile displaying the student's coin balance. Adapts to the
 * active theme (light / dark) via Tailwind `dark:` variants rather than inline
 * styles — necessary because React inline styles don't respond to the `<html
 * class="dark">` cascade on theme toggle. One clickable action: "Top up" →
 * /billing. The card body itself navigates to /wallet.
 */
export function WalletAttributionCard({ coins, plan, compact = false }: Props) {
  const isPro = plan === "pro"
  // Pro keeps the green corner glow; free uses the primary violet. The plan
  // pill itself was removed per owner request — the glow is the only remaining
  // plan hint.
  const glowColor = isPro ? "#22c55e" : "#818cf8"

  const goToWallet = () => {
    window.location.href = "/wallet"
  }

  const padding = compact ? "p-3.5" : "p-5"
  const brandText = compact ? "text-[0.62rem]" : "text-[0.68rem]"
  const balanceText = compact ? "text-[1.5rem]" : "text-[2rem]"
  const buttonText = compact ? "text-[0.72rem]" : "text-[0.8rem]"
  const buttonPad = compact ? "py-2 px-3" : "py-2.5 px-4"

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goToWallet}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          goToWallet()
        }
      }}
      className={[
        "relative w-full flex flex-col cursor-pointer rounded-2xl overflow-hidden",
        padding,
        // Light mode: soft off-white card with slate border + subtle shadow
        "bg-gradient-to-br from-white to-slate-50 border border-slate-200 shadow-md",
        // Dark mode: deep gradient, no border, deep shadow
        "dark:bg-gradient-to-br dark:from-[#1c1c22] dark:to-[#101013] dark:border-0 dark:shadow-[0_10px_30px_-8px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.07)]",
      ].join(" ")}
      style={{
        maxWidth: compact ? undefined : 320,
        margin: compact ? undefined : "0 auto",
      }}
    >
      {/* Static corner glow — toned down in light mode so the card stays clean */}
      <div
        aria-hidden
        className="absolute pointer-events-none rounded-full blur-[36px] opacity-10 dark:opacity-20"
        style={{
          width: compact ? 110 : 150,
          height: compact ? 110 : 150,
          top: compact ? -45 : -55,
          right: compact ? -25 : -35,
          backgroundColor: glowColor,
        }}
      />

      {/* Top row: chip + brand */}
      <div className="relative flex items-center gap-2">
        <svg
          aria-hidden
          viewBox="0 0 24 18"
          className={compact ? "w-5 h-4" : "w-6 h-5"}
        >
          <rect
            x="0.5"
            y="0.5"
            width="23"
            height="17"
            rx="3"
            fill="#d4af37"
            stroke="rgba(0,0,0,0.15)"
          />
          <path
            d="M4 6h16M4 9h16M4 12h16M8 3v12M16 3v12"
            stroke="rgba(0,0,0,0.3)"
            strokeWidth="0.6"
            fill="none"
          />
        </svg>
        <span
          className={[
            "font-medium tracking-wide",
            brandText,
            "text-slate-500 dark:text-white/50",
          ].join(" ")}
        >
          Civil Aid
        </span>
      </div>

      {/* Balance */}
      <div className={compact ? "mt-3.5 relative" : "mt-5 relative"}>
        <p
          className={[
            "text-[0.58rem] font-semibold uppercase tracking-[0.1em] mb-1.5",
            "text-slate-500 dark:text-white/40",
          ].join(" ")}
        >
          Coin balance
        </p>
        <div className="flex items-baseline gap-2">
          <span
            className={[
              balanceText,
              "font-black leading-none tracking-tight tabular-nums",
              "text-slate-900 dark:text-white",
            ].join(" ")}
          >
            {coins.toLocaleString()}
          </span>
          <span
            className={[
              "font-semibold",
              compact ? "text-[0.7rem]" : "text-[0.8rem]",
              "text-slate-500 dark:text-white/45",
            ].join(" ")}
          >
            coins
          </span>
        </div>
      </div>

      {/* Top up button — same violet gradient both themes */}
      <a
        href="/billing"
        onClick={(e) => e.stopPropagation()}
        className={[
          "relative mt-5 flex items-center justify-center gap-1.5 w-full rounded-lg",
          "bg-gradient-to-br from-indigo-500 to-violet-500 text-white font-bold no-underline tracking-wide",
          "hover:from-indigo-400 hover:to-violet-400 transition-colors",
          buttonText,
          buttonPad,
        ].join(" ")}
      >
        <svg
          aria-hidden
          viewBox="0 0 20 20"
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 4v12M4 10h12" />
        </svg>
        Top up
      </a>
    </div>
  )
}
